import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Pencil,
  Trash2,
  RotateCcw,
  Calendar,
  User,
  Phone,
  Wrench,
  FileText,
} from 'lucide-react';
import { useProject } from '@/hooks/projects/use-project';
import { useOptimisationRuns } from '@/hooks/optimisation/use-optimisation-runs';
import { useDeleteProject } from '@/hooks/projects/use-delete-project';
import { useRestoreProject } from '@/hooks/projects/use-restore-project';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { EditProjectModal } from './components/edit-project-modal';
import { ProjectSharePanel } from './components/project-share-panel';
import { LineItemCalculatorForm } from './components/line-item-calculator-form';
import { LineItemTable } from './components/line-item-table';
import { LatestRunPanel } from './components/latest-run-panel';
import { ExportsPanel } from '@/pages/exports/components/exports-panel';

type Tab = 'line-items' | 'optimise' | 'exports' | 'share';

const TABS: { id: Tab; label: string }[] = [
  { id: 'line-items', label: 'Line Items' },
  { id: 'optimise', label: 'Optimise' },
  { id: 'exports', label: 'Exports' },
  { id: 'share', label: 'Share' },
];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const projectId = id ? parseInt(id, 10) : undefined;

  const [activeTab, setActiveTab] = React.useState<Tab>('line-items');
  const [editOpen, setEditOpen] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const { data: project, isLoading, isError } = useProject(projectId);
  const { data: runs } = useOptimisationRuns(projectId ? { projectId, limit: 1 } : undefined);
  const latestCompletedRunId =
    runs?.items[0]?.runStatus === 'COMPLETED' ? runs.items[0].id : undefined;
  const deleteMutation = useDeleteProject();
  const restoreMutation = useRestoreProject();

  const handleDelete = () => {
    if (!projectId) return;
    deleteMutation.mutate(projectId, {
      onSuccess: () => {
        toast({ title: 'Project deleted' });
        navigate('/projects');
      },
      onError: () => toast({ variant: 'destructive', title: 'Failed to delete project' }),
    });
  };

  const handleRestore = () => {
    if (!projectId) return;
    restoreMutation.mutate(projectId, {
      onSuccess: () => toast({ title: 'Project restored' }),
      onError: () => toast({ variant: 'destructive', title: 'Failed to restore project' }),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-6 w-6 animate-spin text-fg-muted" />
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/projects')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Projects
        </Button>
        <Card className="border-dashed">
          <CardContent className="flex h-32 items-center justify-center text-fg-muted text-sm">
            Project not found or failed to load.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Compact Project Header Strip */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-card rounded-lg border border-border shadow-sm">
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/projects')}
            className="h-8 w-8 p-0 text-fg-muted hover:text-fg-primary shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-bold tracking-tight text-fg-primary break-words">
            {project.name}
          </h1>
          {project.isDeleted === 1 && (
            <Badge variant="destructive" className="shrink-0">
              Deleted
            </Badge>
          )}
          {project.reference && (
            <Badge
              variant="outline"
              className="font-mono text-xs font-medium text-fg-muted bg-muted/30 border-border px-2 py-0.5 shrink-0"
            >
              {project.reference}
            </Badge>
          )}

          <div className="hidden md:block h-6 w-px bg-border shrink-0" />

          {/* Metadata Inline Chips */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {project.clientName && (
              <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-fg-secondary">
                <User className="h-3.5 w-3.5 text-fg-muted shrink-0" />
                <span className="font-medium text-fg-primary">{project.clientName}</span>
              </span>
            )}
            {project.clientContact && (
              <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-fg-secondary">
                <Phone className="h-3.5 w-3.5 text-fg-muted shrink-0" />
                <span className="font-medium text-fg-primary">{project.clientContact}</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-fg-secondary">
              <Calendar className="h-3.5 w-3.5 text-fg-muted shrink-0" />
              <span className="font-medium text-fg-primary">{formatDate(project.createdDate)}</span>
            </span>
            {(project.defaultKerfMm != null ||
              project.defaultStockBarMm != null ||
              project.defaultOffcutThresholdMm != null) && (
              <span
                className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-fg-secondary"
                title="Defaults"
              >
                <Wrench className="h-3.5 w-3.5 text-fg-muted shrink-0" />
                <span className="font-medium text-fg-primary">
                  {[
                    project.defaultKerfMm != null && `Kerf ${project.defaultKerfMm}mm`,
                    project.defaultStockBarMm != null && `Bar ${project.defaultStockBarMm}mm`,
                    project.defaultOffcutThresholdMm != null &&
                      `Offcut ≥${project.defaultOffcutThresholdMm}mm`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
            )}
            {project.remarks && (
              <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-fg-secondary">
                <FileText className="h-3.5 w-3.5 text-fg-muted shrink-0" />
                <span
                  className="font-medium text-fg-primary break-words max-w-[200px]"
                  title={project.remarks}
                >
                  {project.remarks}
                </span>
              </span>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 shrink-0 ml-auto sm:ml-0">
          {project.isDeleted === 0 && (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Edit
              </Button>
              {confirmDelete ? (
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                  >
                    {deleteMutation.isPending ? 'Deleting…' : 'Confirm'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmDelete(true)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/20"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Delete
                </Button>
              )}
            </>
          )}
          {project.isDeleted === 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRestore}
              disabled={restoreMutation.isPending}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              {restoreMutation.isPending ? 'Restoring…' : 'Restore'}
            </Button>
          )}
        </div>
      </div>

      {/* Main content area (Tab navigation & active tab contents) */}
      <main className="w-full space-y-4">
        {/* Tab navigation */}
        <div>
          <div className="flex gap-1 border-b border-border">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={[
                  'px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
                  activeTab === tab.id
                    ? 'border-primary text-fg-primary'
                    : 'border-transparent text-fg-secondary hover:text-fg-primary',
                ].join(' ')}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="pt-4">
            {activeTab === 'line-items' && project.isDeleted === 0 && (
              <div className="space-y-4">
                <LineItemCalculatorForm projectId={project.id} />
                <LineItemTable projectId={project.id} />
              </div>
            )}
            {activeTab === 'line-items' && project.isDeleted === 1 && (
              <p className="text-sm text-fg-muted">
                Line items are read-only for deleted projects.
              </p>
            )}

            {activeTab === 'optimise' && (
              <LatestRunPanel projectId={project.id} isDeleted={project.isDeleted === 1} />
            )}

            {activeTab === 'exports' && (
              <ExportsPanel projectId={project.id} runId={latestCompletedRunId} />
            )}

            {activeTab === 'share' && project.isDeleted === 0 && (
              <ProjectSharePanel project={project} />
            )}
            {activeTab === 'share' && project.isDeleted === 1 && (
              <p className="text-sm text-fg-muted">Sharing is disabled for deleted projects.</p>
            )}
          </div>
        </div>
      </main>

      {editOpen && (
        <EditProjectModal open={editOpen} onOpenChange={setEditOpen} project={project} />
      )}
    </div>
  );
}
